// FMO Enrollment Periods - AEP/OEP performance by agency
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Calendar, Users, Target } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_ENROLLMENT_TOUR_STEPS = [
  { type: 'splash', title: 'Enrollment Periods', description: 'Track OEP and AEP outreach performance across all your agencies. See reach rates, contacts made, and which agencies are falling behind on client outreach.' },
  { target: 'fmo-enroll-summary', emoji: '📊', title: 'OEP Summary', description: 'Four key metrics: reach rate (% of eligible clients contacted), clients contacted, total outreach touches, and total eligible clients. Aim for 90%+ reach.', position: 'bottom' },
  { target: 'fmo-enroll-byagency', emoji: '🏢', title: 'By Agency', description: 'Progress bars per agency showing OEP outreach progress. Green (80%+), yellow (50-80%), red (<50%). Agencies in red need immediate attention from their agency owners.', position: 'top' },
  { type: 'celebration', title: 'Every Contact Counts!', description: 'Agencies that achieve 90%+ OEP outreach retain 12% more clients. Share top-performing agency strategies with the underperformers.' },
];

export default function FmoEnrollment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [enTourStep, setEnTourStep] = useState(0);
  const [showEnTour, setShowEnTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-enrollment-tour-dismissed')) setShowEnTour(true);
  }, []);
  const advanceEnTour = useCallback(() => { setEnTourStep(s => { if (s + 1 >= FMO_ENROLLMENT_TOUR_STEPS.length) { localStorage.setItem('fmo-enrollment-tour-dismissed', 'true'); setShowEnTour(false); return 0; } return s + 1; }); }, []);
  const backEnTour = useCallback(() => setEnTourStep(s => Math.max(0, s - 1)), []);
  const dismissEnTour = useCallback(() => { localStorage.setItem('fmo-enrollment-tour-dismissed', 'true'); setShowEnTour(false); }, []);
  useEffect(() => {
    if (!showEnTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissEnTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceEnTour(); if (e.key === 'ArrowLeft') backEnTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showEnTour, advanceEnTour, backEnTour, dismissEnTour]);

  const fetchEnrollment = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/enrollment`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) { console.error('[FMO Enrollment]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEnrollment(); }, [fetchEnrollment]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  const oep = data?.oep || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2"><Calendar className="text-purple-600" /> Enrollment Periods</h1>
              <p className="text-sm text-gray-500">AEP/OEP performance across agencies</p>
            </div>
          </div>
          <button onClick={fetchEnrollment} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-gray-500" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* OEP Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-tour="fmo-enroll-summary">
          <div className="bg-white rounded-xl border-t-4 border-purple-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">OEP Reach Rate</p>
            <p className="text-3xl font-bold text-purple-600">{oep.reachRate}%</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-blue-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">Clients Contacted</p>
            <p className="text-3xl font-bold text-blue-600">{oep.clientsContacted}</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-green-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">Total Touches</p>
            <p className="text-3xl font-bold text-green-600">{oep.totalTouches}</p>
          </div>
          <div className="bg-white rounded-xl border-t-4 border-amber-500 shadow-sm p-5">
            <p className="text-sm text-gray-500">Eligible Clients</p>
            <p className="text-3xl font-bold text-[#172A3A]">{oep.totalEligible}</p>
          </div>
        </div>

        {/* By Agency */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-enroll-byagency">
          <h3 className="font-semibold text-gray-900 mb-4">Agency OEP Performance</h3>
          <div className="space-y-4">
            {(data?.byAgency || []).map(a => (
              <div key={a.agencyId}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{a.agencyName}</span>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-gray-500">{a.contacted}/{a.totalClients}</span>
                    <span className={`font-bold ${a.reachRate >= 80 ? 'text-green-600' : a.reachRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {a.reachRate}%
                    </span>
                  </div>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${
                    a.reachRate >= 80 ? 'bg-green-500' : a.reachRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`} style={{ width: `${a.reachRate}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showEnTour && (
        <TourBubble stepData={FMO_ENROLLMENT_TOUR_STEPS[enTourStep]} stepIndex={enTourStep} totalSteps={FMO_ENROLLMENT_TOUR_STEPS.length}
          onNext={advanceEnTour} onBack={backEnTour} onDismiss={dismissEnTour}
          splashIcon={<Calendar className="w-10 h-10 text-purple-600" />} maskId="fmo-enrollment-tour-mask" />
      )}
    </div>
  );
}
